import {
  Box,
  Flex,
  ScaleFade,
  Spinner,
  Text,
  type FlexProps,
  type ResponsiveValue,
} from '@chakra-ui/react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { MaterialCardContextMenu } from '$/components/core/Collection/MaterialCard/MaterialCardContextMenu';
import { type Material } from '$/services/usecases/materials/mapper/materials';
import { getColorBrightness } from '$/utils/colorUtils';

interface Props {
  material: Material;
  pageSize: number;
  setActiveMaterial: (material: Material) => void;
  height: ResponsiveValue<string>;
  activeMaterialId?: string;
  loadingMaterialId?: string;
  borderRadius?: string;
  isHovered?: boolean;
  hideFavoriteButton?: boolean;
}

export const FanPresentationItem: FC<Props & FlexProps> = ({
  material,
  pageSize,
  setActiveMaterial,
  height,
  activeMaterialId,
  loadingMaterialId,
  borderRadius,
  isHovered = false,
  hideFavoriteButton = false,
  ...props
}) => {
  const { t } = useTranslation();

  if (material.isUnavailable) {
    return (
      <Flex
        pos='relative'
        w='full'
        h={height}
        bg='transparent'
        border='1px solid'
        borderColor='border'
        borderRadius={pageSize === 1 ? borderRadius : '0'}
        _first={{
          borderRadius: `${borderRadius} 0 0 ${borderRadius}`,
        }}
        _last={{
          borderRadius: `0 ${borderRadius} ${borderRadius} 0`,
        }}
        _notLast={{ borderRightColor: 'transparent' }}
        aria-hidden='true'
        css={{
          ':only-child': {
            borderRadius: borderRadius,
          },
        }}
        {...props}
      />
    );
  }

  const isActive = activeMaterialId === material.id;
  const isLoading = loadingMaterialId === material.id;
  const isLight = getColorBrightness(material.hex) > 180;

  return (
    <Flex
      key={material.id}
      pos='relative'
      align='flex-end'
      justify='center'
      w='full'
      h={height}
      bg={material.url({ size: '300' }) ?? material.hex}
      border='1px solid'
      borderColor='border'
      borderRadius={pageSize === 1 ? borderRadius : '0'}
      _hover={
        isActive
          ? undefined
          : {
              borderColor: 'borderHover',
              _notLast: { borderRightColor: 'borderHover' },
            }
      }
      _first={{
        borderRadius: `${borderRadius} 0 0 ${borderRadius}`,
      }}
      _last={{
        borderRadius: `0 ${borderRadius} ${borderRadius} 0`,
      }}
      _notLast={{
        borderRightColor: 'transparent',
      }}
      cursor='pointer'
      transition='width 0.2s linear'
      aria-busy={isLoading}
      aria-label={material.name}
      aria-pressed={isActive}
      css={{
        ':only-child': {
          borderRadius: borderRadius,
        },
      }}
      onClick={() => setActiveMaterial(material)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setActiveMaterial(material);
        }
      }}
      role='button'
      tabIndex={0}
      {...props}
    >
      <ScaleFade
        delay={{
          enter: 0.1,
          exit: 0,
        }}
        in={isHovered}
        initialScale={0.7}
      >
        <Text
          mb='2'
          color={
            getColorBrightness(material.hex) > 180
              ? 'neutral.1000'
              : 'neutral.50'
          }
          fontSize='sm'
        >
          {material.name}
        </Text>
      </ScaleFade>
      {isHovered && !hideFavoriteButton && (
        <MaterialCardContextMenu
          editMode='favorite'
          material={material}
          materialType='color_tint'
          bg='bodyBackground'
          top='8px'
          right='8px'
        />
      )}
      {isLoading && (
        <Flex
          pos='absolute'
          zIndex='1'
          align='center'
          justify='center'
          pointerEvents='none'
          inset='0'
        >
          <Spinner
            color={isLight ? 'black' : 'white'}
            aria-label={t('general.loading')}
            emptyColor='transparent'
            size='md'
            speed='0.65s'
            thickness='3px'
          />
        </Flex>
      )}
      {isActive && (
        <Box
          pos='absolute'
          zIndex='2'
          borderRadius='inherit'
          shadow={`inset 0 0 0 1px ${isLight ? 'white' : 'black'}, inset 0 0 0 4px ${isLight ? 'black' : 'white'}`}
          pointerEvents='none'
          inset='-1px'
        />
      )}
    </Flex>
  );
};
